import React from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  Text,
  useBreakpointValue,
  useColorModeValue,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import { ReactTyped } from 'react-typed';

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionButton = motion(Button);
const MotionWrap = motion(Wrap);

const fadeInUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
  }
};

const staggerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

const skills = ['Java', 'Gradle', 'Maven', 'React', 'HTML/CSS', 'Javascript', 'Git'];

export default function Hero() {
  const textColor = useColorModeValue('gray.800', 'gray.200');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const tagBg = useColorModeValue('gray.100', 'gray.700');
  const gradientText = useColorModeValue(
    'linear(to-r, #200122, #6f0000)',
    'linear(to-r, #bdc3c7, white)'
  );

  const buttonSize = useBreakpointValue({ base: 'md', md: 'lg' });
  const isMobile = useBreakpointValue({ base: true, md: false });

  const scrollToProjects = (e) => {
    e.preventDefault();
    const projectsSection = document.querySelector('#projects');
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box w="100%" py={20}>
      <Container maxW="container.md">
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={staggerVariants}
        >
          <VStack spacing={8} align="start">
            <MotionHeading
              as="h1"
              size="3xl"
              fontWeight="bold"
              bgGradient={gradientText}
              bgClip="text"
              variants={fadeInUpVariants}
            >
              Moi Riccardo
            </MotionHeading>
            <MotionHeading
              as="h2"
              size="xl"
              fontWeight="medium"
              color={subtitleColor}
              variants={fadeInUpVariants}
            >
              <ReactTyped
                strings={[
                  'Front-End Developer',
                  'Java Developer',
                  'Tech Enthusiast'
                ]}
                typeSpeed={40}
                backSpeed={50}
                loop
              />
            </MotionHeading>
            <MotionText
              fontSize="xl"
              color={textColor}
              variants={fadeInUpVariants}
            >
              Ciao, sono Riccardo, un appassionato di tecnologia e sviluppo dal
              2020. Mi dedico alla creazione di esperienze web innovative e allo
              sviluppo di soluzioni Java robuste.
            </MotionText>
            <MotionWrap spacing={4} variants={fadeInUpVariants}>
              {skills.map((skill, index) => (
                <WrapItem key={index}>
                  <Box
                    px={3}
                    py={1}
                    bg={tagBg}
                    color={textColor}
                    fontWeight="medium"
                    fontSize="sm"
                    borderRadius="full"
                  >
                    {skill}
                  </Box>
                </WrapItem>
              ))}
            </MotionWrap>
            <HStack spacing={4} width="100%" flexDirection={{ base: 'column', md: 'row' }}>
              <MotionButton
                rightIcon={<ArrowForwardIcon />}
                bg={useColorModeValue('black', 'white')}
                color={useColorModeValue('white', 'black')}
                size={buttonSize}
                fontWeight="medium"
                _hover={{
                  bg: useColorModeValue('gray.800', 'gray.200'),
                }}
                variants={fadeInUpVariants}
                onClick={scrollToProjects}
                width={{ base: '100%', md: 'auto' }}
              >
                Esplora i miei progetti
              </MotionButton>
              <HStack spacing={4} width={{ base: '100%', md: 'auto' }}>
                <MotionButton
                  as="a"
                  href="https://github.com/rickylachow"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="ghost"
                  size={buttonSize}
                  variants={fadeInUpVariants}
                  width={{ base: '100%', md: 'auto' }}
                >
                  <Icon as={FaGithub} boxSize={6} />
                  {isMobile && <Text ml={2}>GitHub</Text>}
                </MotionButton>
                <MotionButton
                  as="a"
                  href="https://www.linkedin.com/in/riccardo-moi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="ghost"
                  size={buttonSize}
                  variants={fadeInUpVariants}
                  width={{ base: '100%', md: 'auto' }}
                >
                  <Icon as={FaLinkedin} boxSize={6} />
                  {isMobile && <Text ml={2}>LinkedIn</Text>}
                </MotionButton>
              </HStack>
            </HStack>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
}